import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

const CONFIRM = "MIGRATE CONNECT TO OWNER";

// TEMPORARY admin tool — remove together with backend/routes/admin_migrations.py.
export default function AdminConnectMigration() {
  const [verify, setVerify] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || `${name} failed`); }
    finally { setBusy(""); }
  };
  const doVerify = () => run("verify", async () => { const { data } = await api.get("/admin/migrations/connect-owner/verify"); setVerify(data); });
  const doPreview = () => run("preview", async () => { const { data } = await api.get("/admin/migrations/connect-owner/preview"); setPreview(data); setApplyResult(null); });
  const doApply = () => run("apply", async () => {
    const { data } = await api.post("/admin/migrations/connect-owner/apply", { plan_id: preview.plan_id, confirm_text: confirmText });
    setApplyResult(data); setConfirmText(""); toast.success(`Migration ${data.state}: ${data.inserted} owner account(s) created`);
    await doVerify();
  });

  useEffect(() => { doVerify(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canApply = preview && preview.summary?.to_migrate > 0 && confirmText === CONFIRM && busy === "";

  return (
    <div className="space-y-6" data-testid="connect-migration-panel">
      <div className="card-elegant p-5 flex gap-3 items-start">
        <ShieldAlert className="w-5 h-5 mt-0.5 text-[color:var(--accent)] shrink-0" />
        <div className="text-sm">
          <div className="font-serif text-lg">Stripe Connect → owner-level migration (one-time)</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">
            Writes only <code>connect_accounts</code>, <code>connect_account_conflicts</code>, <code>admin_migration_runs</code>. Payment transactions and earnings are never modified — compare the digests before and after. Conflicting accounts are recorded, never merged.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full h-10" onClick={doVerify} disabled={!!busy} data-testid="migration-verify-btn">
          {busy === "verify" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} 1 · Verify (read-only)
        </Button>
        <Button variant="outline" className="rounded-full h-10" onClick={doPreview} disabled={!!busy} data-testid="migration-preview-btn">
          {busy === "preview" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} 2 · Preview plan (read-only)
        </Button>
      </div>

      {verify && (
        <Section title="Verify" testid="migration-verify-output">
          <Row k="Owner-level accounts" v={verify.connect_accounts} />
          <Row k="Open conflicts" v={verify.open_conflicts} />
          <Row k="Consistent with legacy listing data" v={verify.consistent ? "yes" : `NO — ${verify.inconsistent_owners.join(", ")}`} />
          <Row k="Owners with legacy account but no owner row" v={verify.owners_with_legacy_account_but_no_owner_row.length ? verify.owners_with_legacy_account_but_no_owner_row.join(", ") : "none"} />
          <Row k="payment_transactions digest" v={`${verify.immutability_digests.payment_transactions.count} rows · ${verify.immutability_digests.payment_transactions.sha256}`} mono />
          <Row k="vendor_earnings digest" v={`${verify.immutability_digests.vendor_earnings.count} rows · ${verify.immutability_digests.vendor_earnings.sha256}`} mono />
          {verify.owners.map((o) => (
            <div key={o.owner} className="text-xs font-mono pl-3" data-testid="migration-verify-owner">{o.owner} → {o.account} · charges_enabled={String(o.charges_enabled)} · listings {o.listings.join(", ")}</div>
          ))}
          {verify.last_run && <Row k="Last run" v={`${verify.last_run.run_id} · ${verify.last_run.state} · ${verify.last_run.finished_at || verify.last_run.started_at}`} mono />}
        </Section>
      )}

      {preview && (
        <Section title="Preview plan" testid="migration-preview-output">
          <Row k="plan_id" v={preview.plan_id} mono />
          <Row k="To migrate / already / conflicts / no owner" v={`${preview.summary.to_migrate} / ${preview.summary.already_migrated} / ${preview.summary.conflicts} / ${preview.summary.skipped_no_owner}`} />
          {preview.to_migrate.map((m) => (
            <div key={m.owner} className="text-xs font-mono pl-3" data-testid="migration-plan-row">+ {m.owner} → {m.account} · from {m.from_listings.join(", ")} · charges_enabled={String(m.flags.charges_enabled)}</div>
          ))}
          {preview.conflicts.map((c) => (
            <div key={c.owner} className="text-xs font-mono pl-3 text-[color:var(--accent)]" data-testid="migration-conflict-row">! {c.owner} {c.kind === "account_shared_across_owners" ? "shares a Stripe account with another owner" : `has ${c.accounts.length} different Stripe accounts`} — {c.action}</div>
          ))}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={`Type ${CONFIRM}`} className="max-w-sm h-10 bg-white font-mono text-xs" data-testid="migration-confirm-input" />
            <Button className="btn-sage rounded-full h-10" onClick={doApply} disabled={!canApply} data-testid="migration-apply-btn">
              {busy === "apply" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} 3 · Apply plan
            </Button>
          </div>
          <div className="text-[11px] text-[color:var(--text-muted)]">Apply is only accepted for the designated migration admin, with this exact plan_id, and the typed confirmation.</div>
        </Section>
      )}

      {applyResult && (
        <Section title="Apply result" testid="migration-apply-output">
          <Row k="run_id / state" v={`${applyResult.run_id} · ${applyResult.state}`} mono />
          <Row k="Owner accounts created / conflicts recorded" v={`${applyResult.inserted} / ${applyResult.conflicts_recorded}`} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, testid, children }) {
  return <div className="card-elegant p-5 space-y-1.5" data-testid={testid}><div className="eyebrow mb-2">{title}</div>{children}</div>;
}
function Row({ k, v, mono }) {
  return <div className="flex flex-wrap justify-between gap-2 text-sm"><span className="text-[color:var(--text-muted)]">{k}</span><span className={mono ? "font-mono text-xs break-all" : ""}>{String(v)}</span></div>;
}
